import React, { useState, useEffect, useRef } from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles, TextField, Typography } from '@material-ui/core'
import { debounce } from 'lodash'
import useHouseholds from '../../hooks/useHouseholds'
import useSelectedFacility from '../../hooks/useSelectedFacility'
import ClientBlock from './components/ClientBlock'

const useStyles = makeStyles(() => ({
	householdSearch: {
		'& .MuiAutocomplete-inputRoot': {
			paddingTop: '8px'
		}
	}
}))

const ClientAutocomplete = ({ onHouseholdSelected, displayAddress = true, additionalSearchParams = {} }) => {
	const classes = useStyles()

	const { facility } = useSelectedFacility()
	const { households, loading, getHouseholdsByMarketId, getHouseholdsWithAdvancedSearch, clearHouseholds } =
		useHouseholds()
	const [household, setHousehold] = useState(null)

	const [open, setOpen] = React.useState(false)
	const [search, setSearch] = useState('')

	const delayedSetSearch = useRef(debounce((t) => setSearch(t), 750)).current

	const updateSearch = (value) => delayedSetSearch(value)

	useEffect(() => {
		if (search && facility) {
			getHouseholdsWithAdvancedSearch({
				marketId: facility.market_id,
				search: encodeURIComponent(search),
				searchBy: 'contact',
				...additionalSearchParams
			})
			getHouseholdsByMarketId(facility.market_id, encodeURIComponent(search), encodeURIComponent('Contact'))
		} else {
			clearHouseholds()
		}
	}, [search, facility, additionalSearchParams])

	useEffect(() => {
		if (!open) {
			clearHouseholds()
			setSearch('')
		}
	}, [open])

	const handleHouseholdSelected = (household) => {
		setHousehold(household)
		if (onHouseholdSelected) {
			onHouseholdSelected(household)
		}
	}

	const clearHousehold = () => {
		handleHouseholdSelected(null)
	}

	return !household ? (
		<Autocomplete
			id="client-select"
			getOptionLabel={(option) =>
				typeof option === 'string' ? option?.account?.full_name : option.account.full_name || 'No Name'
			}
			filterOptions={(x) => x}
			options={households && households.length ? households.filter((h) => h.account) : []}
			className={classes.householdSearch}
			autoComplete
			noOptionsText="No other contacts"
			loading={loading}
			includeInputInList
			disableOpenOnFocus
			open={open}
			onOpen={() => {
				setOpen(true)
			}}
			onClose={() => {
				setOpen(false)
			}}
			onChange={(event, newValue) => {
				handleHouseholdSelected(newValue)
			}}
			renderOption={(option) => (
				<React.Fragment>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<Typography variant="body2">{option?.account?.full_name}</Typography>
						<Typography style={{ fontSize: '10px' }}>{option?.account?.email}</Typography>
					</div>
				</React.Fragment>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Search existing clients"
					variant="filled"
					fullWidth
					onChange={(e) => updateSearch(e.target.value)}
				/>
			)}
		/>
	) : (
		<ClientBlock household={household} onClearHousehold={clearHousehold} displayAddress={displayAddress} />
	)
}

export default ClientAutocomplete
